























import { Vue } from "vue/types/vue";
import Component, { mixins } from "vue-class-component";
import { Prop, Provide, Watch } from "vue-property-decorator";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { MessagesSearchResult } from "@scrile/api-provider/dist/api/MessagesProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Subscription, SubscriptionsSearchResult } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole, checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import { Throttle } from "@/lib/decorators";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { EVENTS } from "@/const";
import MessagesView from "./components/MessagesView.vue";

@Component({ name: "PageMessages", components: { MessagesView } })
export default class PageMessages extends mixins(WithAuthUser) {
  @Prop({ required: true })
  public readonly userId!: string;

  public loading: boolean = false;

  public allowManageMessages: boolean = false;

  public enableScroll: boolean = false;

  public processingPin: boolean = false;

  public checkConvertingStatusTimeoutId: ReturnType<typeof setTimeout> | null = null;

  public messagesSearchResult: MessagesSearchResult<Message> | null = null;

  public messageThread: MessageThread | null = null;

  public user: UserPublicData | null = null;

  public userCallOptions: CallOptions | null = null;

  public paymentPackages: PaymentPackage[] = [];

  public allowResponse: boolean = true;

  public opponentSubscription: Subscription | null = null;

  @Provide("messageData")
  public providedData: { user: UserPublicData | null } = {
    user: null,
  };

  @Watch("user")
  public onUserChanged(user: UserPublicData | null) {
    this.providedData.user = user;
  }

  public get messageList(): Message[] {
    return Array.from<Message>(this.messagesSearchResult?.result ?? []).reverse();
  }

  public get convertingMessages(): Message[] {
    return (this.messagesSearchResult?.result ?? []).filter(
      msg =>
        msg.attachment.length > 0 && msg.attachment.some(f => ["NEW", "CONVERTING"].includes(f.file.processingStatus))
    );
  }

  public get minUnreadMessageId() {
    return this.messageThread?.minUnreadMessageId ?? "0";
  }

  public async togglePin() {
    if (!this.messageThread || this.processingPin) return;
    try {
      this.processingPin = true;
      const pinned = await providers.MessageThreadsProvider.togglePin({ threadId: this.messageThread.id });
      emitter.emit(EVENTS.NOTIFICATION_SHOW, {
        text: pinned ? $t("Marked as important") : $t("Marked as common"),
        timeout: 2000,
      });
      this.$set(this.messageThread, "pinTime", pinned ? new Date().toISOString() : null);
    } finally {
      this.processingPin = false;
    }
  }

  @Watch("messagesSearchResult", { deep: true })
  public watchingMessages() {
    const ids = (this.convertingMessages ?? []).map(i => i.id);
    if (ids.length > 0 && !this.checkConvertingStatusTimeoutId) {
      this.checkConvertingStatus();
    }
  }

  @Throttle(1000, { leading: false })
  public markRead(message: Message) {
    emitter.emit(EVENTS.MESSAGES_MARK_READ, message);
  }

  public cleanTimeout() {
    this.checkConvertingStatusTimeoutId && clearTimeout(this.checkConvertingStatusTimeoutId);
    this.checkConvertingStatusTimeoutId = null;
  }

  public checkConvertingStatus() {
    this.cleanTimeout();

    this.checkConvertingStatusTimeoutId = setTimeout(async () => {
      const ids = (this.convertingMessages ?? []).map(i => i.id);

      if (ids.length === 0) {
        this.cleanTimeout();
        return;
      }
      const updatedMessages = await providers.MessagesProvider.find({ data: { ids } });
      if (updatedMessages.result.length > 0 && this.messagesSearchResult) {
        this.$set(
          this.messagesSearchResult,
          "result",
          this.messagesSearchResult.result.map(i => updatedMessages.result.find(item => item.id === i.id) ?? i)
        );
      }
      this.checkConvertingStatus();
    }, 3000);
  }

  public markedRead(message: Message) {
    if (!this.messageThread) return;
    let id = Number(message.id);
    const minimumId = Number(this.minUnreadMessageId);
    if (minimumId > 0 || minimumId < id) {
      const lastMessage = this.messageList[this.messageList.length - 1];
      if (lastMessage.id === message.id) id = -1;
      this.$set(this.messageThread, "minUnreadMessageId", id + 1 + "");
    }
  }

  public async getPrevMessages(cb: Function) {
    try {
      if (!this.messagesSearchResult) return;
      await this.getMessages(this.messagesSearchResult.page + 1, false);
    } finally {
      cb();
    }
  }

  public async getMessages(page: number = 0, replace = true) {
    this.enableScroll = false;
    try {
      if (!this.messageThread) {
        try {
          providers.MessagesProvider.setReceiveErrors(false);
          this.messageThread = await providers.MessageThreadsProvider.findPersonal({ opponentId: this.userId });
          this.user = ((this.messageThread.users as any) as UserPublicData[]).find(i => i.id === this.userId) || null;
        } catch (e) {}
      }
      if (!this.user) {
        const response = await providers.UserPublicDataProvider.search({ data: { id: [this.userId] } });
        this.user = response.result.find(i => i.id === this.userId) || null;
      }
      if (!this.user) {
        this.$router.push({ name: "messageThreads" });
        return;
      }
      if (!this.messageThread) return;
      const response = await providers.MessagesProvider.find({
        data: {
          threadId: this.messageThread.id,
          includeDeleted: false,
          sort: "TIME,DESC",
          page,
        },
      });
      this.messagesSearchResult = {
        ...response,
        result:
          !replace && this.messagesSearchResult
            ? this.messagesSearchResult.result.concat(response.result)
            : response.result,
      };
    } finally {
      this.enableScroll = this.messagesSearchResult?.hasNextPage ?? false;
    }
  }

  public addMessage(message: Message) {
    if (!this.messageThread || !this.messagesSearchResult) {
      return this.getMessages();
    }
    const messageList = this.messagesSearchResult.result;
    this.$set(this.messagesSearchResult, "result", [message, ...messageList]);
    if (this.messagesSearchResult.hasNextPage) {
      this.messagesSearchResult.result.pop();
    }
  }

  public async removeMessage(messageId: string) {
    if (this.messagesSearchResult && this.messageThread) {
      const index = this.messagesSearchResult.result.findIndex(i => i.id === messageId);
      if (index !== -1) {
        this.messagesSearchResult.result.splice(index, 1);
        this.$set(this.messagesSearchResult, "totalRecords", this.messagesSearchResult.result.length - 1);

        if (this.messagesSearchResult.result.length === 0) {
          this.messageThread = null;
          this.messagesSearchResult = null;
        } else if (this.messagesSearchResult.hasNextPage) {
          const nextPageMessageSeachResult = await providers.MessagesProvider.find({
            data: {
              threadId: this.messageThread.id,
              includeDeleted: false,
              sort: "TIME,DESC",
              page: this.messagesSearchResult.result.length,
              size: 1,
            },
          });
          this.messagesSearchResult.result.splice(
            this.messagesSearchResult.result.length,
            0,
            nextPageMessageSeachResult.result[0]
          );
          this.$set(this.messagesSearchResult, "hasNextPage", nextPageMessageSeachResult.hasNextPage);
        }
      }
    }
  }

  public async receivedMessage(message: Message) {
    if (message.senderId === "0" && !this.messageThread) {
      await this.getMessages();
    }

    if (
      message.senderId === this.authUserStrict.id ||
      this.messageThread?.id !== message.threadId ||
      this.messageList.find(m => m.id === message.id)
    ) {
      return;
    }
    this.addMessage(message);
    await this.$nextTick();
    (this.$refs.messagesView as Vue).$emit("receivedMessage");
    if (this.messageThread && this.messageThread.minUnreadMessageId === "0") {
      this.$set(this.messageThread, "minUnreadMessageId", message.id);
    }
  }

  public async updatePurchasedMessage(messageId: string) {
    if (!this.messagesSearchResult) return;
    const response = await providers.MessagesProvider.find({ data: { ids: [messageId] } });
    const updatedMessage = response.result[0];
    this.$set(
      this.messagesSearchResult,
      "result",
      this.messagesSearchResult.result.map(message => (message.id === updatedMessage.id ? updatedMessage : message))
    );
  }

  public async getPageData() {
    try {
      this.loading = true;

      const getMessagePrivateSettings = async () => {
        if (!checkAuthorities(this.authUserAuthorities, ["getMyMessagePrivateUserSettings"])) return;
        const response = await providers.MessageSettingsProvider.searchPrivate({ userId: this.authUserStrict.id });
        this.allowManageMessages = response.allowManagement;
      };
      const getCallOptions = async () => {
        if (!config.featureCalling) return;
        this.userCallOptions =
          (await providers.CallOptionsProvider.find({ userIds: [this.userId] })).find(i => i.userId === this.userId) ??
          null;
      };
      const getMessageSettings = async (userId: string) => {
        const settings = await providers.MessageSettingsProvider.search({ userIds: [userId], size: 1 });
        this.allowResponse = settings.result.find(i => i.userId === userId)?.allowIncoming ?? true;
      };
      const getOpponentSubscription = async () => {
        if (!checkRole(this.authUserStrict.role || "", "creator")) return;
        const opponentSubscriptionResponse: SubscriptionsSearchResult = await providers.SubscriptionProvider.search({
          data: {
            consumerIds: [this.userId],
            providerId: this.authUserStrict.id ?? "",
            expired: false,
            size: 1,
          },
        });
        this.opponentSubscription = opponentSubscriptionResponse.result.length
          ? opponentSubscriptionResponse.result[0]
          : null;
      };
      await Promise.all<void>([
        this.getMessages(),
        getMessagePrivateSettings(),
        getOpponentSubscription(),
        getCallOptions(),
        getMessageSettings(this.userId),
        providers.PaymentPackageProvider.findAll({ type: [PaymentPackageEnum.MESSAGE_ATTACHMENT] }).then(r => {
          this.paymentPackages = r;
        }),
      ]);
    } finally {
      this.loading = false;
    }
  }

  @Watch("userId")
  public watchUserId() {
    this.loading = false;
    this.enableScroll = false;
    this.messagesSearchResult = null;
    this.messageThread = null;
    this.user = null;
    this.userCallOptions = null;
    this.paymentPackages = [];
    this.getPageData();
  }

  public async created() {
    await this.getPageData();
    emitter.on<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
    emitter.on<any>(EVENTS.MESSAGES_MARKED_READ, this.markedRead);
    emitter.on<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, this.updatePurchasedMessage);
  }

  public mounted() {
    emitter.emit(EVENTS.MAIN_MENU_HIDE, true);
  }

  public beforeDestroy() {
    emitter.off<any>(EVENTS.MESSAGES_NEW_MESSAGE, this.receivedMessage);
    emitter.off<any>(EVENTS.MESSAGES_MARKED_READ, this.markedRead);
    emitter.off<any>(EVENTS.MESSAGES_UPDATE_MESSAGE, this.updatePurchasedMessage);
    emitter.emit(EVENTS.MAIN_MENU_HIDE, false);
    this.cleanTimeout();
  }
}
